import type { CaptureDeviceClass } from './measurements';

/**
 * Face shape: what a customer's face was judged to be, by whom, and when.
 *
 * A classification, not a measurement. There is no ground truth for face shape — it is an industry
 * convention, taxonomies differ between vendors, and two opticians will disagree about the same
 * face. That is why the record keeps a person's decision as the answer and never promotes a
 * machine's suggestion on its own, and why nothing here carries a millimetre.
 */

/**
 * The face-shape vocabulary.
 *
 * Deliberately not `FrameShape`, which describes the glasses. They share the words ROUND, SQUARE and
 * OVAL and mean opposite things by them — the conventional advice pairs a round face with a frame
 * that is not round — so joining the two would invert every recommendation. Phase 4D holds the
 * mapping between them, explicitly.
 */
export const FaceShape = {
  OVAL: 'OVAL',
  ROUND: 'ROUND',
  SQUARE: 'SQUARE',
  OBLONG: 'OBLONG',
  HEART: 'HEART',
  DIAMOND: 'DIAMOND',
  TRIANGLE: 'TRIANGLE',
  /** Genuinely between two named shapes. Both are kept, never collapsed into one. */
  MIXED: 'MIXED',
  /** No usable answer: nothing matched, or the optician declined to label the face. A result, not an error. */
  UNKNOWN: 'UNKNOWN',
} as const;
export type FaceShape = (typeof FaceShape)[keyof typeof FaceShape];

/** The seven shapes that can stand alone. MIXED is built from two of these; UNKNOWN from none. */
export const NAMED_FACE_SHAPES = [
  FaceShape.OVAL,
  FaceShape.ROUND,
  FaceShape.SQUARE,
  FaceShape.OBLONG,
  FaceShape.HEART,
  FaceShape.DIAMOND,
  FaceShape.TRIANGLE,
] as const;
export type NamedFaceShape = (typeof NAMED_FACE_SHAPES)[number];

export const isNamedFaceShape = (shape: FaceShape): shape is NamedFaceShape =>
  (NAMED_FACE_SHAPES as readonly FaceShape[]).includes(shape);

/**
 * How an assessment was arrived at.
 *
 * Its own enum rather than `MeasurementSource`: that one describes how a *number* was acquired
 * (which camera, which device), and reusing it here would blur acquiring a measurement with judging
 * a shape. `CAMERA` exists so the column and the contracts are ready for 4C.4; nothing produces one yet.
 */
export const FaceAssessmentSource = {
  /** An optician looked at the customer and chose. No camera involved, and no suggestion exists.  */
  MANUAL: 'MANUAL',
  /** Suggested by the camera and then confirmed by a person. Arrives with 4C.4. */
  CAMERA: 'CAMERA',
} as const;
export type FaceAssessmentSource = (typeof FaceAssessmentSource)[keyof typeof FaceAssessmentSource];

export const FaceAssessmentStatus = { ACTIVE: 'ACTIVE', VOIDED: 'VOIDED' } as const;
export type FaceAssessmentStatus = (typeof FaceAssessmentStatus)[keyof typeof FaceAssessmentStatus];

/** One assessment as the desk reads it. */
/**
 * Where a machine suggestion's image came from.
 *
 * Acquisition, not authority: a suggestion from any of the three is still only a suggestion until a
 * person confirms it, and `confirmedShape` means the same thing whichever produced it.
 */
export const FaceCaptureInput = {
  LIVE_CAMERA: 'LIVE_CAMERA',
  PHONE_QR: 'PHONE_QR',
  PHOTO: 'PHOTO',
} as const;
export type FaceCaptureInput = (typeof FaceCaptureInput)[keyof typeof FaceCaptureInput];

export interface CustomerFaceAssessmentSummary {
  id: string;
  customerId: string;
  source: FaceAssessmentSource;
  /** Null exactly when the source is MANUAL: a judgement by eye has no image behind it. */
  captureInput: FaceCaptureInput | null;
  status: FaceAssessmentStatus;

  /**
   * What the camera proposed, and how well its rule matched.
   *
   * Null throughout for a manual assessment — a manual row must never carry an invented suggestion.
   * The scores are independent rule-match figures in basis points: they do not sum to 10 000, and
   * they are not probabilities, accuracy or confidence that anyone will agree.
   */
  suggestedShape: FaceShape | null;
  suggestedRunnerUpShape: FaceShape | null;
  suggestedTopScoreBp: number | null;
  suggestedRunnerUpScoreBp: number | null;
  /** Both present exactly when the suggestion is MIXED. */
  suggestedComponentA: FaceShape | null;
  suggestedComponentB: FaceShape | null;
  reasonCodes: string[];
  /** The three versions behind a suggestion. */
  algorithmVersion: string | null;
  geometryVersion: string | null;
  gateConfigVersion: string | null;
  /** Aggregate capture facts. How the capture went, never how right the answer is. */
  acceptedFrameCount: number | null;
  captureDurationMs: number | null;

  /** What a person decided. This is the answer; everything above is context for it. */
  confirmedShape: FaceShape | null;
  /** Present exactly when `confirmedShape` is MIXED, and then both are named and different. */
  confirmedComponentA: FaceShape | null;
  confirmedComponentB: FaceShape | null;
  confirmedByName: string | null;
  confirmedAt: string | null;

  assessedAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  /** True when a newer active confirmed assessment exists. Derived on read, never stored. */
  isSuperseded: boolean;
}

/**
 * The whole of what Phase 4D may see.
 *
 * Deliberately narrow. No suggestion, no score, no reason code, no geometry and no classifier
 * version crosses this boundary — 4D advises on what a person confirmed, or it advises on nothing.
 */
export interface CurrentFaceShape {
  assessmentId: string;
  confirmedShape: FaceShape;
  /** Both named and different when the shape is MIXED; null otherwise. */
  confirmedComponentA: FaceShape | null;
  confirmedComponentB: FaceShape | null;
  confirmedAt: string;
}

/**
 * Whether a confirmed shape can drive styling advice.
 *
 * UNKNOWN is a legitimate thing for an optician to record and a legitimate thing for 4D to receive —
 * it simply carries no styling information, and must never be treated as an eighth shape.
 */
export const yieldsStylingShape = (current: CurrentFaceShape | null): boolean =>
  current !== null && current.confirmedShape !== FaceShape.UNKNOWN;

// ---------------------------------------------------------------------------
// The camera path
// ---------------------------------------------------------------------------

/**
 * Every reason a suggestion may give, as a closed set.
 *
 * An allowlist rather than free text, and enforced three times over: the DTO rejects anything not on
 * it, a database CHECK rejects it again, and `face-reason-codes.spec.ts` fails if this list and the
 * classifier's own enum ever drift apart. The point is that a device cannot put a sentence — or a
 * measurement dressed as a sentence — into the record. The screen turns each code into one whole
 * translated phrase from its own resources.
 */
export const FaceReasonCode = {
  FACE_LONGER_THAN_WIDE: 'FACE_LONGER_THAN_WIDE',
  FACE_CLEARLY_LONGER_THAN_WIDE: 'FACE_CLEARLY_LONGER_THAN_WIDE',
  FACE_AS_WIDE_AS_LONG: 'FACE_AS_WIDE_AS_LONG',
  STRAIGHT_SIDES: 'STRAIGHT_SIDES',
  CURVED_SIDES: 'CURVED_SIDES',
  ANGULAR_JAW: 'ANGULAR_JAW',
  SOFT_JAW: 'SOFT_JAW',
  WIDE_JAW: 'WIDE_JAW',
  NARROW_JAW: 'NARROW_JAW',
  WIDE_FOREHEAD: 'WIDE_FOREHEAD',
  NARROW_FOREHEAD: 'NARROW_FOREHEAD',
  FOREHEAD_BALANCED_WITH_CHEEKS: 'FOREHEAD_BALANCED_WITH_CHEEKS',
  WIDEST_AT_CHEEKBONES: 'WIDEST_AT_CHEEKBONES',
  WIDENS_TOWARDS_JAW: 'WIDENS_TOWARDS_JAW',
  NARROWS_TOWARDS_CHIN: 'NARROWS_TOWARDS_CHIN',
  POINTED_CHIN: 'POINTED_CHIN',
  BROAD_CHIN: 'BROAD_CHIN',
} as const;
export type FaceReasonCode = (typeof FaceReasonCode)[keyof typeof FaceReasonCode];

export const FACE_REASON_CODES: readonly FaceReasonCode[] = Object.values(FaceReasonCode);

/** A rule-match score. Independent of every other shape's, and never a probability. */
export const MAX_RULE_MATCH_SCORE_BP = 10_000;

/**
 * What a capture device may say about one face, and the whole of it.
 *
 * Deliberately a closed, typed shape with no open container anywhere: no map, no JSON blob, no
 * free-text field. Everything a device could use to smuggle geometry out — a ratio, a landmark, a
 * point, a crop, a per-frame value — has nowhere to go, and the DTO rejects any property not listed
 * here rather than ignoring it.
 *
 * `acceptedFrameCount` and `captureDurationMs` are the only aggregate capture facts kept. They
 * describe how the capture went, not how right the answer is: capture quality is not classification
 * correctness, and neither number is evidence about the shape.
 */
export interface FaceCandidateInput {
  /** Chosen by the device, so a resent submission is recognised rather than duplicated. */
  submissionId: string;

  /** What the classifier suggested: one of the seven, MIXED, or UNKNOWN. Never a confirmed value. */
  suggestedShape: FaceShape;
  /** Both present exactly when the suggestion is MIXED, and then named and different. */
  suggestedComponentA?: NamedFaceShape;
  suggestedComponentB?: NamedFaceShape;
  /** The next best shape when a single one was suggested. Absent for MIXED and UNKNOWN. */
  runnerUpShape?: NamedFaceShape;

  /** 0–10 000 each, independent. They are not normalised and do not sum to 10 000. */
  topRuleMatchScoreBp: number;
  runnerUpRuleMatchScoreBp: number;

  reasonCodes: FaceReasonCode[];

  /** The three versions that produced this, so an answer can always be explained later. */
  classifierVersion: string;
  geometryVersion: string;
  gateConfigVersion: string;

  /** Aggregate capture facts. Not a quality score, and not evidence about the shape. */
  acceptedFrameCount: number;
  captureDurationMs: number;

  deviceClass: CaptureDeviceClass;
}

/** A suggestion waiting for a person, as the desk reads it. Read only through the authenticated desk API. */
export interface FaceCandidateSummary {
  submissionId: string;
  suggestedShape: FaceShape;
  suggestedComponentA: NamedFaceShape | null;
  suggestedComponentB: NamedFaceShape | null;
  runnerUpShape: NamedFaceShape | null;
  topRuleMatchScoreBp: number;
  runnerUpRuleMatchScoreBp: number;
  reasonCodes: FaceReasonCode[];
  classifierVersion: string;
  geometryVersion: string;
  gateConfigVersion: string;
  acceptedFrameCount: number;
  captureDurationMs: number;
  submittedAt: string;
}

/**
 * How the platform has released camera face analysis to a tenant.
 *
 * The same three states, and the same platform-only control, as camera PD — because the reason is
 * the same. The classifier has passed synthetic and property testing only; no real-face, multi-rater
 * evidence exists yet, so ENABLED is not a statement that it classifies correctly. Manual assessment
 * is unaffected by this setting and always available.
 */
export const FaceAnalysisMode = {
  OFF: 'OFF',
  /** A supervised pilot. Suggestions are produced and always confirmed by a person. */
  VALIDATION: 'VALIDATION',
  ENABLED: 'ENABLED',
} as const;
export type FaceAnalysisMode = (typeof FaceAnalysisMode)[keyof typeof FaceAnalysisMode];
